import { IonContent, IonPage, useIonRouter } from '@ionic/react';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { S3_PROFILE_BASE as S3_BASE } from 'common/common';
import ListSheetLeaderboard from './ListSheetLeaderboard';
import LeaderboardFeatureCard from './LeaderboardFeatureCard';
import { CasinoService, LeadboardMetric, LeadboardWindow } from 'services/casino';
import { ILeaderBoardReport } from 'appRedux/models/casinoModels';
import { IState, ITabProperties } from 'appRedux/createStore';
import {
  FilterTypeEnum,
  MapTypeEnum,
  OrderByEnum,
  TimeFrameValueEnum,
} from 'common/common';
import { PRIVATE_LEADERBOARD_PLAYER_NAME } from 'common/commonEnum';
import { ILeaderBoardList } from '@interfaces/services';
import { FilterReduxCommandCreator } from 'appRedux/actions/filterCommandCreator';
import { LeaderBoardInsightValue, TimeFrameValue } from 'common/types';
import useLeaderboardFilters from './views/Leaderboard/hooks/useLeaderboardFilters';
import LinkPlayerWrapper from 'pages/MyPlayerCards/LinkPlayerWrapper';
import FloorMap from 'components/FloorMapPackage/components/floorMap';
import useFetchGeoJson from 'common/reactHooks/useFetchGeoJson';
import LeaderboardCurrentUserCard from './LeaderboardCurrentUserCard';
import { useHistory } from 'react-router';
import { parseValue, formatCurrencyWithCents } from 'common/include';
import CasinoNavBar from 'components/CasinoNavBar';
import LeaderboardTopLayer from './LeaderboardTopLayer';
import LeaderboardSectionTitle from './LeaderboardSectionTitle';
import LeaderWinnings from './LeaderWinnings';
import { LeaderboardPlayerName } from './PrivateLeaderboardPlayerLabel';

const typedParseValue = parseValue as (
  value: number,
  insight: LeaderBoardInsightValue
) => string | number;

const typedFormatCurrencyWithCents = formatCurrencyWithCents as (value: number) => string;

type LeaderboardRowStyle = 'gold' | 'silver' | 'bronze' | 'default' | 'highlight';

interface LeaderboardRowUI {
  rank: number;
  player: string;
  avatarText: string;
  avatarId?: number;
  avatarBgId?: number;
  metric: string;
  style: LeaderboardRowStyle;
  isCurrentUser: boolean;
}

interface LeaderboardCurrentUserUI {
  player: string;
  avatarText: string;
  avatarId?: number;
  avatarBgId?: number;
  rank: number;
  metric: string;
}

const getRowStyle = (row: ILeaderBoardReport, index: number): LeaderboardRowStyle => {
  if (index === 0) return 'gold';
  if (index === 1) return 'silver';
  if (index === 2) return 'bronze';
  if (row.is_current_user) return 'highlight';
  return 'default';
};

const formatNumber = (value: number | undefined): string =>
  `${Math.round(value || 0).toLocaleString()}`;

const insightSlides: LeaderBoardInsightValue[] = [
  'jackpot_value',
  'money_won',
  'spins',
  'avg_money_won_per_spin',
];

const getNormalizedInsight = (insight: string | undefined): LeaderBoardInsightValue => {
  switch (insight) {
    case 'jackpot_value':
    case 'money_won':
    case 'spins':
    case 'avg_money_won_per_spin':
      return insight;
    default:
      return 'money_won';
  }
};

const getNormalizedTimeframe = (timeframe: TimeFrameValue | undefined): TimeFrameValue => {
  switch (timeframe) {
    case TimeFrameValueEnum.months1:
    case TimeFrameValueEnum.weeks1:
    case TimeFrameValueEnum.hours24:
      return timeframe;
    default:
      return TimeFrameValueEnum.months1;
  }
};

const getColumnHeader = (insight: LeaderBoardInsightValue): string => {
  switch (insight) {
    case 'jackpot_value':
      return '$ | # Jackpots';
    case 'money_won':
      return '$ Won';
    case 'avg_money_won_per_spin':
      return '$ Won / Spin';
    case 'spins':
    default:
      return 'Spins';
  }
};

const getRowMetricValue = (row: ILeaderBoardReport, insight: LeaderBoardInsightValue): number => {
  switch (insight) {
    case 'jackpot_value':
      return row.jackpot_value;
    case 'money_won':
      return row.money_won;
    case 'avg_money_won_per_spin':
      return row.avg_money_won_per_spin;
    case 'spins':
    default:
      return row.spins;
  }
};

const getRowRankByInsight = (row: ILeaderBoardReport, insight: LeaderBoardInsightValue): number => {
  switch (insight) {
    case 'jackpot_value':
      return Number(row.jackpot_value_order || row.orderNumber || 0);
    case 'money_won':
      return Number(row.money_won_order || row.orderNumber || 0);
    case 'avg_money_won_per_spin':
      return Number(row.avg_money_won_per_spin_order || row.orderNumber || 0);
    case 'spins':
    default:
      return Number(row.spins_order || row.orderNumber || 0);
  }
};

const MONEY_INSIGHTS: LeaderBoardInsightValue[] = [
  'money_won',
  'avg_money_won_per_spin',
  'jackpot_value',
];

const formatRowMetric = (value: number, insight: LeaderBoardInsightValue): string => {
  if (MONEY_INSIGHTS.includes(insight)) {
    return typedFormatCurrencyWithCents(value);
  }
  return String(typedParseValue(value, insight));
};

const METRIC_BY_INSIGHT: Record<LeaderBoardInsightValue, LeadboardMetric> = {
  money_won: 'MostWon',
  jackpot_value: 'MostJackpot',
  spins: 'MostSpins',
  avg_money_won_per_spin: 'MostWonBySpin',
};

const WINDOW_BY_TIMEFRAME: Partial<Record<TimeFrameValue, LeadboardWindow>> = {
  [TimeFrameValueEnum.hours24]: '24h',
  [TimeFrameValueEnum.weeks1]: '7d',
  [TimeFrameValueEnum.months1]: '30d',
};

const formatLeaderboardMetric = (
  row: ILeaderBoardReport,
  insight: LeaderBoardInsightValue
): string => {
  // GetLeadboards only has a single metric_value (no jackpot count).
  if (typeof row.metric_value === 'number' && Number.isFinite(row.metric_value)) {
    if (insight === 'spins') {
      return String(typedParseValue(row.metric_value, insight));
    }
    return typedFormatCurrencyWithCents(row.metric_value);
  }

  if (insight === 'jackpot_value') {
    const jackpotValue = typedFormatCurrencyWithCents(Number(row.jackpot_value || 0));
    const jackpots = formatNumber(Number(row.jackpots || 0));
    return `${jackpotValue} | ${jackpots}`;
  }

  return formatRowMetric(getRowMetricValue(row, insight), insight);
};

const getAvatarText = (row: ILeaderBoardReport): string => {
  const initials = (row.alias_initial2 || row.alias_initial || '').trim();
  if (initials) return initials.slice(0, 2).toUpperCase();

  const fallback = (row.alias_name || '').trim();
  if (!fallback) return 'P';

  return fallback.slice(0, 2).toUpperCase();
};

const getRankBadgeImage = (rank: number): string | null => {
  if (rank === 1) return '/assets/images/rank-gold.svg';
  if (rank === 2) return '/assets/images/rank-silver.svg';
  if (rank === 3) return '/assets/images/rank-bronze.svg';
  if (rank >= 4 && rank <= 10) return '/assets/images/rank-badge.svg';
  return null;
};

const durationOptions: { label: string; value: TimeFrameValue }[] = [
  { label: '30 days', value: TimeFrameValueEnum.months1 },
  { label: '7 days', value: TimeFrameValueEnum.weeks1 },
  { label: '24 hours', value: TimeFrameValueEnum.hours24 },
];

const LeaderboardScreen: React.FC<{ isDarkMode?: boolean; styles?: object }> = ({
  isDarkMode = false,
  styles,
}) => {
  const casinoSvcRef = React.useRef<CasinoService>(new CasinoService());
  const routerHistory = useHistory();
  const ionRouter = useIonRouter();
  const dispatch = useDispatch();
  const filterCommandCreator = React.useMemo(() => FilterReduxCommandCreator(dispatch), [dispatch]);
  const { sortBy, activeFilter } = useLeaderboardFilters();

  const casinoId = useSelector<IState>(
    (state) => state.app.persistedState.casinoSchema?.kpCasinoPk
  ) as number;
  const userId = useSelector<IState>((state) => state.app.persistedState.userInfo?.appUserPk) as
    | string
    | undefined;
  const isCasinoAccount = useSelector<IState>(
    (state) => state.app.persistedState.userInfo?.isCasinoAccount
  ) as boolean;
  const isSlotcheckInternal = useSelector<IState>(
    (state) => state.app.persistedState.userInfo?.groups?.includes('SlotcheckInternal')
  ) as boolean;
  const isPlayerLinked = useSelector<IState>(
    (state) => state.app.persistedState.linkPlayerState?.isPlayerLinked
  ) as boolean;
  const leaderboardTabAnalytics = useSelector<IState>(
    (state) => state.app.persistedState.filterState?.[FilterTypeEnum.leaderboard]
  ) as ITabProperties | undefined;
  const selectedSlots = useSelector<IState>(
    (state) => state.app.persistedState.slotState.selectedSlots
  ) as string[];
  const isProUser = useSelector<IState>((state) => state.app.persistedState.userInfo?.isProUser) as
    | boolean
    | undefined;

  // Set default leaderboard on first load.
  // Note: Redux seeds insight to 'return' (not a leaderboard insight), so we
  // always apply the leaderboard default on first mount. All users default to
  // Most $ Won.
  const hasAppliedDefaultInsight = React.useRef(false);
  React.useEffect(() => {
    if (isProUser === undefined || hasAppliedDefaultInsight.current) return;
    hasAppliedDefaultInsight.current = true;
    filterCommandCreator.SetFilterOptions(FilterTypeEnum.leaderboard, {
      insight: 'money_won',
    });
  }, [filterCommandCreator, isProUser]);

  const [rows, setRows] = React.useState<LeaderboardRowUI[]>([]);
  const [currentUserRow, setCurrentUserRow] = React.useState<LeaderboardCurrentUserUI | null>(null);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [isFloorMapLoading, setIsFloorMapLoading] = React.useState<boolean>(false);
  const { geoJsonData, getGeoJsonLoading, slotMetaData } = useFetchGeoJson(casinoId);

  const selectedInsight = getNormalizedInsight(leaderboardTabAnalytics?.insight);
  const selectedTimeframe = getNormalizedTimeframe(leaderboardTabAnalytics?.timeframe);
  const hasLeaderboardAccess = Boolean(isCasinoAccount || isPlayerLinked || isSlotcheckInternal);
  const selectedOrderBy = activeFilter === 'rank' ? sortBy : OrderByEnum.Asc;
  const metricHeader = getColumnHeader(selectedInsight);

  const activeSlide = React.useMemo(() => {
    const slideIndex = insightSlides.findIndex((insight) => insight === selectedInsight);
    return slideIndex >= 0 ? slideIndex : 1;
  }, [selectedInsight]);
  const isMapRefreshing = isFloorMapLoading || getGeoJsonLoading;

  const handleSlideChange = (slideIndex: number) => {
    let nextInsight: LeaderBoardInsightValue;
    switch (slideIndex) {
      case 0:
        nextInsight = 'jackpot_value';
        break;
      case 1:
        nextInsight = 'money_won';
        break;
      case 2:
        nextInsight = 'spins';
        break;
      case 3:
      default:
        nextInsight = 'avg_money_won_per_spin';
        break;
    }
    if (!nextInsight || nextInsight === selectedInsight) return;
    filterCommandCreator.SetFilterOptions(FilterTypeEnum.leaderboard, {
      insight: nextInsight,
    });
  };

  const handleDurationChange = (timeframeValue: TimeFrameValue) => {
    if (timeframeValue === selectedTimeframe) return;
    filterCommandCreator.SetFilterOptions(FilterTypeEnum.leaderboard, {
      timeframe: timeframeValue,
    });
  };

  const renderLeaderboardContent = () => {
    if (!hasLeaderboardAccess) {
      return <LinkPlayerWrapper IsShowMaybeLater={false} />;
    }

    return (
      <>
        <LeaderboardFeatureCard
          durationOptions={durationOptions}
          activeDuration={selectedTimeframe}
          activeSlide={activeSlide}
          onSlideChange={handleSlideChange}
          onDurationChange={handleDurationChange}
        />
        {/* <EventsFeatureActionRow onExploreClick={openFeaturedChallenge} showText={false} /> */}

        <div
          className={`leaderboard-section ${!isLoading && rows.length > 0 ? 'leaderboard-section--has-data' : ''}`}
        >
          {!isLoading && (
            <LeaderboardSectionTitle
              title="Top 50 Leaderboard"
              titleClassName="leaderboard-header-title"
            />
          )}

          <div className="leaderboard-table">
            {!isLoading && rows.length > 0 && (
              <div className="leaderboard-table__header">
                <span>Rank</span>
                <span>Player</span>
                <span className="leaderboard-table__header-metric">{metricHeader}</span>
                <span className="leaderboard-table__header-badge" aria-hidden="true" />
              </div>
            )}
            <div className="leaderboard-table__rows">
              {isLoading ? (
                <div className="leaderboard-table__state leaderboard-table__state--loading">
                  <span className="leaderboard-table__spinner" aria-hidden="true" />
                  <span>Loading leaderboard...</span>
                </div>
              ) : rows.length === 0 ? (
                <div className="leaderboard-table__state">No results found.</div>
              ) : (
                rows.map((row) => {
                  const badgeImage = getRankBadgeImage(row.rank);
                  const hasTopThreeBackground =
                    row.isCurrentUser &&
                    (row.style === 'gold' || row.style === 'silver' || row.style === 'bronze');

                  return (
                    <div
                      key={`${row.rank}-${row.player}`}
                      className={`leaderboard-row leaderboard-row--${row.style} ${
                        row.isCurrentUser ? 'leaderboard-row--currentuser' : ''
                      }`}
                    >
                      {hasTopThreeBackground && (
                        <div className="leaderboard-row__top3-fill" aria-hidden="true" />
                      )}
                      <div className="leaderboard-row__rank">{row.rank}</div>
                      <div className="leaderboard-row__player">
                        {row.avatarId ? (
                          <span
                            className="leaderboard-row__avatar leaderboard-row__avatar--img"
                            style={{ backgroundImage: `url(${S3_BASE}/avatar-bg${row.avatarBgId}.png)` }}
                          >
                            <img
                              src={`${S3_BASE}/avatar${row.avatarId}.png`}
                              alt=""
                              className="leaderboard-row__avatar-img"
                            />
                          </span>
                        ) : (
                          <span className="leaderboard-row__avatar">{row.avatarText}</span>
                        )}
                        <LeaderboardPlayerName name={row.player} />
                      </div>
                      <div className="leaderboard-row__won">{row.metric}</div>
                      <div className="leaderboard-row__badge">
                        {badgeImage && (
                          <img
                            src={badgeImage}
                            alt={`Rank ${row.rank} badge`}
                            className="leaderboard-row__badge-image"
                          />
                        )}
                        {row.rank >= 4 && row.rank <= 10 && (
                          <span className="leaderboard-row__badge-rank" aria-hidden="true">
                            {row.rank}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </>
    );
  };

  React.useEffect(() => {
    if (!hasLeaderboardAccess || !casinoId || !userId) {
      setRows([]);
      setCurrentUserRow(null);
      setIsLoading(false);
      return;
    }

    const windowName = WINDOW_BY_TIMEFRAME[selectedTimeframe] || '30d';
    const params = {
      casinoId,
      metric: METRIC_BY_INSIGHT[selectedInsight],
      window_name: windowName,
    };

    setIsLoading(true);

    const subscription = casinoSvcRef.current.GetLeadboards(params).subscribe({
      next: (response: void | ILeaderBoardList<ILeaderBoardReport>) => {
        const data: ILeaderBoardReport[] = response?.DataSet ?? [];
        const playerData = (
          response as ILeaderBoardList<ILeaderBoardReport> & {
            PlayerDataSet?: ILeaderBoardReport[];
          }
        )?.PlayerDataSet;
        const currentUser = playerData?.[0] || data.find((row) => row.is_current_user);

        const mappedRows: LeaderboardRowUI[] = data.map(
          (row: ILeaderBoardReport, index: number) => ({
            rank: Number(row.orderNumber || index + 1),
            player: row.alias_name || row.alias_initial2 || PRIVATE_LEADERBOARD_PLAYER_NAME,
            avatarText: getAvatarText(row),
            avatarId: row.avatarId,
            avatarBgId: row.avatarBgId,
            metric: formatLeaderboardMetric(row, selectedInsight),
            style: getRowStyle(row, index),
            isCurrentUser: Boolean(row.is_current_user),
          })
        );

        if (currentUser) {
          setCurrentUserRow({
            rank: getRowRankByInsight(currentUser, selectedInsight),
            player: currentUser.alias_name || currentUser.alias_initial2 || PRIVATE_LEADERBOARD_PLAYER_NAME,
            avatarText: getAvatarText(currentUser),
            avatarId: currentUser.avatarId,
            avatarBgId: currentUser.avatarBgId,
            metric: formatLeaderboardMetric(currentUser, selectedInsight),
          });
        } else {
          setCurrentUserRow(null);
        }

        setRows(mappedRows);
        setIsLoading(false);
      },
      error: () => {
        setRows([]);
        setCurrentUserRow(null);
        setIsLoading(false);
      },
    });

    return () => subscription.unsubscribe();
  }, [casinoId, userId, hasLeaderboardAccess, selectedInsight, selectedTimeframe]);

  return (
    <IonPage className="leaderboard-tab">
      <IonContent className="leaderboard-tab__content" fullscreen>
        <CasinoNavBar styles={styles} isDarkMode={isDarkMode} />
        {hasLeaderboardAccess && (
          <div className="leaderboard-mapstats">
            {isMapRefreshing && (
              <div className="leaderboard-mapstats__loader" role="status" aria-live="polite">
                <span className="leaderboard-table__spinner" aria-hidden="true" />
                <span>Refreshing map...</span>
              </div>
            )}

            {!isMapRefreshing && (
              <FloorMap
                mapId={MapTypeEnum.slotReportMap}
                slotData={[]}
                slotDataIsLoading={isFloorMapLoading}
                sortDirection={selectedOrderBy}
                shouldHideSlotMark={true}
                selectedInsight={selectedInsight}
                selectedTimeFrame={selectedTimeframe}
                isShowData={true}
                selectedSlots={selectedSlots}
                geoJsonData={geoJsonData}
                getGeoJsonLoading={getGeoJsonLoading}
                slotMetaData={slotMetaData}
                onClickHeatFilter={() => null}
                onClickSlot={() => null}
                setFloormapLoading={setIsFloorMapLoading}
              />
            )}
          </div>
        )}

        {hasLeaderboardAccess && !isLoading && currentUserRow && (
          <LeaderboardCurrentUserCard
            player={currentUserRow.player}
            avatarText={currentUserRow.avatarText}
            rank={currentUserRow.rank}
            metricLabel={metricHeader}
            metricValue={currentUserRow.metric}
          />
        )}

        <ListSheetLeaderboard>
          {/* <div className="leaderboard-timer">20 days : 15 hours : 07 min</div> */}
          <LeaderboardTopLayer
            title="LEADERBOARDS"
            showBack={true}
            onBack={() => routerHistory.goBack()}
            showBadge={true}
            activeDotPage={0}
            onDotClick={(page) => {
              if (page === 1) ionRouter.push('/tabs/challenges', 'forward', 'push');
            }}
            onSwipe={(dir) => {
              if (dir === 'left') ionRouter.push('/tabs/challenges', 'forward', 'push');
            }}
          />
          {renderLeaderboardContent()}
        </ListSheetLeaderboard>
      </IonContent>
    </IonPage>
  );
};

export default LeaderboardScreen;
